"use client";

import { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import axiosInstance from "../../helpers/Axios";
import baseURL from "../../config/config";

// ----------------- Styled Components -----------------
const StyledBox = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 30vh;
  padding: 2rem;
  background-color: #f3f4f6;
  margin-top: 5rem;
`;

const FormContainer = styled(Box)`
  background-color: white;
  padding: 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-width: 28rem;
`;

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 0.75rem;
    margin: 1rem;
  }
`;

const ResultCard = styled(Box)`
  background-color: #f9fafb;
  padding: 1rem;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
`;

const StyledButton = styled(Button)`
  background-color: #2563eb;
  &:hover {
    background-color: #1d4ed8;
  }
`;

// ----------------- Component -----------------
const UploadPassageExplanationUpdate = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [open, setOpen] = useState(false);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      alert("Please upload an Excel file.");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axiosInstance.post(
        `${baseURL}/api/trail/bulk-explanation-update/`,
        formData
      );

      const backend = res.data?.data ?? {};

      // normalize structure
      const normalized = {
        message: backend.message ?? "",
        updated: backend.updated ?? [],
        not_found: backend.not_found ?? [],
        failed: backend.failed ?? [],
        summary: backend.summary ?? {},
        errors: res.data?.errors ?? [],
      };

      setResponseData(normalized);
    } catch (error) {
      setResponseData({
        error:
          error.response?.data?.error ||
          "Upload failed! Please try again.",
      });
    } finally {
      setLoading(false);
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setResponseData(null);
    setFile(null);
  };

  // ----------------- Render Result List -----------------
  const renderResults = () => {
    if (
      !responseData?.updated?.length &&
      !responseData?.not_found?.length &&
      !responseData?.failed?.length
    )
      return null;

    const groups = [
      { key: "updated", label: "Updated", color: "green" },
      { key: "not_found", label: "Not Found", color: "yellow" },
      { key: "failed", label: "Failed", color: "red" },
    ];

    return (
      <div className="mt-4 border border-gray-200 p-3 rounded-lg">
        <Typography variant="h6" className="mb-3 text-gray-800 font-medium">
          Processing Details
        </Typography>

        <div className="max-h-80 overflow-y-auto pr-1">
          {groups.map((group) => {
            const items = responseData[group.key] ?? [];
            if (!items.length) return null;

            return (
              <div key={group.key} className="mb-4">
                <Typography className="mb-2 font-semibold text-gray-700">
                  {group.label} ({items.length})
                </Typography>

                {items.map((item, idx) => {
                  let icon = <InfoIcon />;
                  let color = "text-gray-700 bg-gray-50 border-gray-200";

                  if (group.key === "updated") {
                    icon = <CheckCircleIcon />;
                    color = "text-green-700 bg-green-50 border-green-200";
                  }
                  if (group.key === "failed") {
                    icon = <ErrorIcon />;
                    color = "text-red-700 bg-red-50 border-red-200";
                  }
                  if (group.key === "not_found") {
                    icon = <ErrorIcon />;
                    color = "text-yellow-700 bg-yellow-50 border-yellow-200";
                  }

                  return (
                    <div
                      key={idx}
                      className={`p-3 rounded-lg border mb-2 flex items-center justify-between ${color}`}
                    >
                      <div className="flex items-center gap-2">
                        {icon}
                        <span className="font-medium">{item.title}</span>
                      </div>

                      {item.error && (
                        <span className="text-xs text-gray-600">
                          {item.error}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ----------------- Main UI -----------------
  return (
    <StyledBox>
      <FormContainer>
        <Typography
          variant="h4"
          className="text-center mb-6 font-semibold text-gray-800"
        >
          Bulk Explanation Update
        </Typography>

        <form onSubmit={handleSubmit} className="space-y-6">
          <TextField
            type="file"
            label="Excel File (.xlsx)"
            accept=".xlsx, .xls"
            onChange={handleFileChange}
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <DescriptionIcon className="w-5 h-5 text-gray-400 mr-2" />
              ),
            }}
          />

          <StyledButton
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Upload & Update"
            )}
          </StyledButton>
        </form>
      </FormContainer>

      <StyledDialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <div className="relative">
          <div className="px-6 py-4 border-b flex justify-between items-center">
            <Typography variant="h6" className="text-gray-800">
              Update Summary
            </Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </div>

          <DialogContent className="px-6 py-5">
            {responseData ? (
              <>
                {/* Summary Top Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <ResultCard>
                    <Typography variant="subtitle2" className="text-gray-500">
                      Updated
                    </Typography>
                    <Typography variant="h6" className="text-green-600">
                      {responseData.summary?.updated ?? 0}
                    </Typography>
                  </ResultCard>

                  <ResultCard>
                    <Typography variant="subtitle2" className="text-gray-500">
                      Not Found
                    </Typography>
                    <Typography variant="h6" className="text-yellow-600">
                      {responseData.summary?.not_found ?? 0}
                    </Typography>
                  </ResultCard>

                  <ResultCard>
                    <Typography variant="subtitle2" className="text-gray-500">
                      Failed
                    </Typography>
                    <Typography variant="h6" className="text-red-600">
                      {responseData.summary?.failed ?? 0}
                    </Typography>
                  </ResultCard>

                  <ResultCard>
                    <Typography variant="subtitle2" className="text-gray-500">
                      Total
                    </Typography>
                    <Typography variant="h6">
                      {responseData.summary?.total_rows ?? 0}
                    </Typography>
                  </ResultCard>
                </div>

                {renderResults()}
              </>
            ) : (
              <Typography>No data received.</Typography>
            )}
          </DialogContent>

          <div className="border-t px-6 py-4 bg-gray-50 flex justify-end">
            <StyledButton onClick={handleClose} variant="contained">
              Close
            </StyledButton>
          </div>
        </div>
      </StyledDialog>
    </StyledBox>
  );
};

export default UploadPassageExplanationUpdate;

